import { useEffect, useState } from 'react'
import { Settings2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { FormGroup } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { normalizeExpectedEntities } from './assignmentTimeline'

export type NoteSettingsValue = {
  message?: string
  ttsMessage?: string
  expectedEntities?: number
}

type Props = {
  label?: string
  message?: string
  ttsMessage?: string
  expectedEntities?: number
  messagePlaceholder: string
  ttsPlaceholder: string
  expectedEntitiesPlaceholder?: number
  canEdit: boolean
  useNoteLabel?: boolean
  onSave: (next: NoteSettingsValue) => void
  onOpenChange?: (open: boolean) => void
}

const AssignmentNoteSettingsDialog = ({
  label,
  message,
  ttsMessage,
  expectedEntities,
  messagePlaceholder,
  ttsPlaceholder,
  expectedEntitiesPlaceholder,
  canEdit,
  useNoteLabel = false,
  onSave,
  onOpenChange,
}: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [messageDraft, setMessageDraft] = useState(message ?? '')
  const [ttsMessageDraft, setTtsMessageDraft] = useState(ttsMessage ?? '')
  const [expectedEntitiesDraft, setExpectedEntitiesDraft] = useState(
    expectedEntities?.toString() ?? '',
  )

  useEffect(() => {
    if (open) return
    setMessageDraft(message ?? '')
    setTtsMessageDraft(ttsMessage ?? '')
    setExpectedEntitiesDraft(expectedEntities?.toString() ?? '')
  }, [message, ttsMessage, expectedEntities, open])

  const commit = () => {
    const nextMessage = messageDraft.trim()
    const nextTtsMessage = ttsMessageDraft.trim()
    onSave({
      message: nextMessage.length ? nextMessage : undefined,
      ttsMessage: nextTtsMessage.length ? nextTtsMessage : undefined,
      expectedEntities: normalizeExpectedEntities(expectedEntitiesDraft),
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) commit()
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='custom'
          className='h-7 w-7 p-0'
        >
          <Settings2Icon className='h-3.5 w-3.5' />
        </Button>
      </DialogTrigger>
      <DialogContent className='w-[44rem] max-w-[44rem]'>
        <DialogHeader>
          <DialogTitle>
            {label
              ? t('dashboard.team.plans.assignments.note-settings-for', {
                  label,
                })
              : t('dashboard.team.plans.assignments.note-settings')}
          </DialogTitle>
        </DialogHeader>

        <FormGroup>
          <div className='grow'>
            <Label>
              {t(
                useNoteLabel
                  ? 'dashboard.team.plans.assignments.note-label'
                  : 'dashboard.team.plans.assignments.note-options',
              )}
            </Label>
            <Input
              value={messageDraft}
              onChange={(event) => setMessageDraft(event.target.value)}
              onBlur={commit}
              className='h-8'
              placeholder={messagePlaceholder}
              disabled={!canEdit}
            />
          </div>

          <div className='grow'>
            <Label>
              {t('dashboard.team.plans.assignments.note-tts-options')}
            </Label>
            <Input
              value={ttsMessageDraft}
              onChange={(event) => setTtsMessageDraft(event.target.value)}
              onBlur={commit}
              className='h-8'
              placeholder={messageDraft.trim() || ttsPlaceholder}
              disabled={!canEdit || useNoteLabel}
            />
          </div>

          <div className='w-40'>
            <Label>
              {t('dashboard.team.plans.assignments.expected-player-count')}
            </Label>
            <Input
              type='text'
              inputMode='numeric'
              maxLength={2}
              value={expectedEntitiesDraft}
              onChange={(event) => setExpectedEntitiesDraft(event.target.value)}
              onBlur={commit}
              className='h-8'
              placeholder={expectedEntitiesPlaceholder?.toString()}
              disabled={!canEdit}
            />
          </div>
        </FormGroup>

        <div className='justify-end flex gap-2'>
          <Button variant='outline' onClick={() => handleOpenChange(false)}>
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AssignmentNoteSettingsDialog
